import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  LinearProgress,
  Pagination
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Group as GroupIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Create authenticated axios instance
const authenticatedAxios = (() => {
  const api = axios.create({
    baseURL: '/api',
    headers: { 'Content-Type': 'application/json' }
  });
  
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  
  return api;
})();

// API functions using authenticated axios
const api = {
  getGroups: (courseId, page = 1, pageSize = 40) => {
    const params = { page, limit: pageSize };
    if (courseId && courseId !== 'all') params.course_id = courseId;
    return authenticatedAxios.get('/groups', { params }).then(res => res.data);
  },
  getCourses: () => authenticatedAxios.get('/courses').then(res => res.data),
  getGroupMembers: (groupId) => authenticatedAxios.get(`/students?group_id=${groupId}`).then(res => res.data),
  getGroupStats: async () => {
    try {
      const [groupsRes, studentsRes] = await Promise.all([
        authenticatedAxios.get('/groups'),
        authenticatedAxios.get('/students')
      ]);
      
      return {
        data: {
          total_groups: groupsRes.data.pagination?.total_items || groupsRes.data.data?.length || 0,
          total_students: studentsRes.data.pagination?.total_items || studentsRes.data.data?.length || 0
        }
      };
    } catch (error) {
      console.error('Error fetching group stats:', error);
      return { data: { total_groups: 0, total_students: 0 } };
    }
  }
};

// Group Members Dialog
const GroupMembersDialog = ({ open, onClose, group, members, loading }) => {
  if (!group) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <GroupIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">
            {group.group_name || group.name} - Members ({members.length})
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : members.length > 0 ? (
          <List>
            {members.map((member) => (
              <ListItem key={member.id} divider>
                <ListItemAvatar>
                  <Avatar>
                    {member.firstname?.charAt(0) || member.first_name?.charAt(0) || 'U'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${member.firstname || member.first_name} ${member.lastname || member.last_name}`}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email: {member.email}
                      </Typography>
                      {member.idnumber && (
                        <Typography variant="body2" color="text.secondary">
                          ID Number: {member.idnumber}
                        </Typography>
                      )}
                      {member.phone1 && (
                        <Typography variant="body2" color="text.secondary">
                          Phone: {member.phone1}
                        </Typography>
                      )}
                      {member.role && (
                        <Chip 
                          label={member.role} 
                          size="small" 
                          color={member.role === 'teacher' ? 'secondary' : 'primary'}
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No members found in this group
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Groups Component
const Groups = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [courseFilter, setCourseFilter] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const pageSize = 40;

  // Queries
  const { data: groupsData, isLoading, error } = useQuery(
    ['groups', courseFilter, page],
    () => api.getGroups(courseFilter, page, pageSize),
    {
      // No automatic polling - refresh only on user actions or manual refresh
      keepPreviousData: true
    }
  );

  const { data: coursesData } = useQuery('courses', api.getCourses);

  const handleViewMembers = async (group) => {
    try {
      setMembersLoading(true);
      setSelectedGroup(group);
      setDialogOpen(true);
      
      const response = await api.getGroupMembers(group.id);
      const members = response.success ? response.data : (response.data || response || []);
      setGroupMembers(Array.isArray(members) ? members : []);
    } catch (err) {
      console.error('Error fetching group members:', err);
      toast.error('Failed to load group members');
      setGroupMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedGroup(null);
    setGroupMembers([]);
  };

  const handleDeleteGroup = async (group) => {
    if (window.confirm(`Are you sure you want to delete the group "${group.group_name || group.name}"?\n\nThis action cannot be undone.`)) {
      try {
        await authenticatedAxios.delete(`/groups/${group.id}`);
        toast.success('Group deleted successfully!');
        queryClient.invalidateQueries('groups');
      } catch (error) {
        console.error('Error deleting group:', error);
        toast.error(error.response?.data?.message || 'Failed to delete group');
      }
    }
  };

  // Extract groups array from API response structure
  const groups = groupsData?.data || groupsData || [];
  const courses = coursesData?.data || coursesData || [];
  const pagination = groupsData?.pagination || {};
  const totalPages = pagination.total_pages || Math.ceil((Array.isArray(groups) ? groups.length : 0) / pageSize);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load groups: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontSize: '1.5rem', color: 'rgba(0, 0, 0, 0.87)' }}>
          Groups Management
        </Typography>
        <Box>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => toast.info('Add Group functionality coming soon!')}
              sx={{ mr: 1 }}
            >
              Add Group
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={() => queryClient.invalidateQueries('groups')}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Course Filter</InputLabel>
                <Select
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                  label="Course Filter"
                >
                  <MenuItem value="all">All Courses</MenuItem>
                  {courses.map((course) => (
                    <MenuItem key={course.id} value={course.moodle_id}>
                      {course.fullname || course.full_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Loading */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Groups Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Group Name</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell align="center">Total Members</TableCell>
                  <TableCell>Sync Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <GroupIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="body1" fontWeight="medium">
                          {group.group_name || group.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SchoolIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
                        <Typography variant="body2" color="text.secondary">
                          {group.course_name || 'Unknown Course'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${group.member_count || 0}`}
                        color={group.member_count > 0 ? 'primary' : 'default'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={group.sync_status || 'never'}
                        color={group.sync_status === 'success' ? 'success' : group.sync_status === 'syncing' ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Members">
                        <IconButton
                          size="small"
                          onClick={() => handleViewMembers(group)}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && (
                        <>
                          <Tooltip title="Edit Group">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setSelectedGroup(group);
                                setEditOpen(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Group">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteGroup(group)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages} • {pagination.total_items || groups.length} groups total
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
                showFirstButton 
                showLastButton
                size="small"
              />
            </Box>
          )}

          {/* Show pagination info even on single page if there are groups */}
          {totalPages === 1 && groups.length > 0 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                Showing all {pagination.total_items || groups.length} groups
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Group Members Dialog */}
      <GroupMembersDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        group={selectedGroup}
        members={groupMembers}
        loading={membersLoading}
      />

      {/* Group Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Group - {selectedGroup?.group_name || selectedGroup?.name}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="h6" color="primary" gutterBottom>
              🚧 Coming Soon!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The group edit functionality is currently under development. 
              This will allow you to modify group details such as name, description, 
              and course assignment.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Groups;
